import { toast } from "../toast/toast";
import { Lab, Reagent, User } from "../../../backend/src/types";
import { ENDPOINT } from "../endpoint/endpoint";
import { errorMessage } from "../function/errorMessage";

type ReagentResponse<T> = {
  data: T;
  error?: any;
};

interface GetReagentListPayload {
  selectedLab: Lab;
}

export async function getReagentList(
  payload: GetReagentListPayload,
): Promise<Reagent[]> {
  try {
    const res = await fetch(ENDPOINT.reagent.getReagentList, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const { data, error }: ReagentResponse<Reagent[]> = await res.json();

    if (!res.ok) {
      throw new Error(error ?? "Server Error");
    }

    return data;
  } catch (error) {
    errorMessage("試薬リストの取得に失敗しました", error);
    return [];
  }
}

interface AddReagentPayload {
  user: User;
  updatedReagent: Reagent;
}

export async function addReagent(
  payload: AddReagentPayload,
): Promise<Reagent | null> {
  try {
    const res = await fetch(ENDPOINT.reagent.addReagent, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const { data, error }: ReagentResponse<Reagent> = await res.json();

    if (!res.ok) {
      throw new Error(error ?? "Server Error");
    }

    return data;
  } catch (error) {
    errorMessage("試薬の追加に失敗しました", error);
    return null;
  }
}

interface EditReagentPayload {
  user: User;
  updatedReagent: Reagent;
}

export async function editReagent(
  payload: EditReagentPayload,
): Promise<Reagent | null> {
  try {
    const res = await fetch(ENDPOINT.reagent.editReagent, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    const { data, error }: ReagentResponse<Reagent> = await res.json();

    if (!res.ok) {
      if (error === "小数エラー") {
        toast.error("単位種別が個数の場合、整数のみを入力してください。");
        return null;
      }
      if (error === "最小残量エラー") {
        toast.error("最低残量は容量を超えることができません。");
        return null;
      }
      if (error === "最小残量エラー") {
        toast.error("最低残量は容量を超えることができません。");
        return null;
      }
      throw new Error(error ?? "Server Error");
    }

    return data;
  } catch (error) {
    errorMessage("試薬の編集に失敗しました。", error);
    return null;
  }
}

interface DeleteReagentPayload {
  reagent_id: number;
}

export async function deleteReagent(payload: DeleteReagentPayload) {
  try {
    const res = await fetch(ENDPOINT.reagent.deleteReagent, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (res.status === 204) {
      return;
    }

    if (!res.ok) {
      const { error }: ReagentResponse<null> = await res.json();
      throw new Error(error ?? "Server Error");
    }
  } catch (error) {
    errorMessage("試薬の削除に失敗しました。", error);
  }
}

interface DisposeReagentPayload {
  reagent_id: number;
}

export async function disposeReagent(payload: DisposeReagentPayload) {
  try {
    const res = await fetch(ENDPOINT.reagent.disposeReagent, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (res.status === 204) {
      return;
    }

    if (!res.ok) {
      const { error }: ReagentResponse<null> = await res.json();
      throw new Error(error ?? "Server Error");
    }
  } catch (error) {
    errorMessage("試薬の廃棄に失敗しました。", error);
  }
}
